import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle, Bot, Zap, Users, TrendingUp, Menu, X, Star } from "lucide-react";
import { toast } from "sonner";

const API              = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// ── reCAPTCHA ─────────────────────────────────────────────────────────
const loadRecaptcha = () => new Promise((resolve) => {
  if (window.grecaptcha) { resolve(); return; }
  const s = document.createElement("script");
  s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
  s.onload = resolve;
  document.head.appendChild(s);
});
const getRecaptchaToken = async () => {
  if (!RECAPTCHA_SITE_KEY) return "no_key";
  try {
    await loadRecaptcha();
    return new Promise((res) => {
      window.grecaptcha.ready(async () => {
        const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: "free_scan" });
        res(token);
      });
    });
  } catch { return "error"; }
};

// ── Constants ─────────────────────────────────────────────────────────
const BUSINESS_CATEGORIES = {
  "Food & Hospitality":               ["Restaurant", "Cafe", "Bar / Nightclub", "Catering", "Food Truck", "Bakery"],
  "Home Services":                    ["Plumbing", "Electrical", "HVAC", "Roofing", "Landscaping / Lawn Care", "Cleaning Services", "Pest Control", "General Contractor", "Pool Service", "Painting"],
  "Health & Wellness":                ["Medical / Doctor", "Dental", "Chiropractic", "Physical Therapy", "Gym / Fitness", "Mental Health / Therapy", "Veterinary / Pet Care", "Optometry", "Pharmacy"],
  "Professional Services":            ["Legal / Law Firm", "Accounting / Finance", "Insurance", "Real Estate", "Marketing Agency", "IT Services / Tech Support", "Consulting", "Financial Advisory"],
  "Retail & Commerce":                ["Retail Store", "E-commerce", "Auto Dealership", "Auto Repair", "Pawn Shop", "Jewelry Store"],
  "Transportation, Moving & Rentals": ["Moving Company", "Trucking / Delivery", "Taxi / Rideshare", "Car Rental", "Equipment Rental"],
  "Beauty & Personal Care":           ["Salon", "Barbershop", "Spa / Massage", "Nail Studio", "Tattoo Studio", "Photography", "Wedding Services"],
  "Education & Childcare":            ["Tutoring", "Private School", "Childcare / Daycare", "Music Lessons", "Dance Studio", "Driving School"],
  "Security & Safety":                ["Security Company", "Locksmith", "Fire Protection", "Private Investigation"],
  "Other":                            ["Other"],
};

const AI_PLATFORMS = [
  { name: "ChatGPT",         color: "#10a37f", bg: "#f0fdf4", delay: 0,    scanTime: 650 },
  { name: "Google Gemini",   color: "#4285F4", bg: "#eff6ff", delay: 600,  scanTime: 650 },
  { name: "Google AI",       color: "#EA4335", bg: "#fef2f2", delay: 1200, scanTime: 650 },
  { name: "Perplexity",      color: "#20B8CD", bg: "#ecfeff", delay: 1800, scanTime: 650 },
  { name: "Copilot",         color: "#0078D4", bg: "#eff6ff", delay: 2400, scanTime: 650 },
  { name: "Claude",          color: "#D4764C", bg: "#fff7ed", delay: 3000, scanTime: 650 },
  { name: "Grok",            color: "#0F172A", bg: "#f8fafc", delay: 3600, scanTime: 650 },
  { name: "Meta AI",         color: "#0064E0", bg: "#eff6ff", delay: 4200, scanTime: 650 },
  { name: "Online Presence", color: "#7C3AED", bg: "#f5f3ff", delay: 4800, scanTime: 1400 },
  { name: "Bing",            color: "#008373", bg: "#f0fdfa", delay: 5600, scanTime: 650 },
  { name: "Google Maps",     color: "#34A853", bg: "#f0fdf4", delay: 6200, scanTime: 650 },
  // ORDO AI Score is always last — computed after all platforms
  { name: "ORDO AI Score",   color: "#1E40AF", bg: "#eff6ff", delay: 7200, scanTime: 900, isFinal: true },
];

const TOTAL_SCAN_TIME = 7200 + 900 + 600; // last delay + last scanTime + buffer

const PILLARS = [
  {
    icon: TrendingUp, color: "#1E40AF", iconBg: "bg-blue-100",
    title: "AI Search Ranking Agent", tagline: "Your always-on AI monitor",
    what: "ORDO AI scans your business across 11 AI search platforms, scores your visibility, and generates your action plan automatically. Rank higher, Earn More Business, Scale",
    basic:  ["Weekly score across 11 AI platforms", "Full weekly report", "Action plan you execute", "Score history and leaderboard"],
    growth: ["Bot executes parts of your plan", "Auto optimizes profiles and listings", "Fixes visibility gaps automatically"],
  },
  {
    icon: Zap, color: "#F97316", iconBg: "bg-orange-100",
    title: "Outreach & Citation Agent", tagline: "Your tireless PR machine",
    what: "Finds every blog, directory, and publication that can mention your business. Earn more credibility and business 24/7",
    basic:  ["Full outreach list with targets", "Ready-to-send templates", "Monthly PR action plan", "Citation growth tracking"],
    growth: ["Bot publishes SEO and GEO optimized content", "Auto submits to directories", "Live links sent to your dashboard", "PR pipeline fully managed"],
  },
  {
    icon: Users, color: "#10B981", iconBg: "bg-emerald-100",
    title: "Lead & Client Discovery Agent", tagline: "Find clients before they find you",
    what: "We find local customers, local businesses, and organizations that may need your services. Deploy once and let it run 24/7",
    basic:  ["Monthly scraped client list", "Filtered by niche and location", "Contact info included", "You reach out yourself"],
    growth: ["Bot sends your contact to clients", "Automated outreach sequences", "Monthly contacts report", "Fully hands off lead gen"],
  },
];

const TERMS = `TERMS OF SERVICE — ORDO AI LLC — Last updated: February 2026

By using ORDO AI you agree that each subscription is tied to a single business, all payments are final and non-refundable after the 3-day free trial, and you authorize ORDO AI to act as your representative in contacting directories, publications, and potential business contacts. ORDO AI does not guarantee specific ranking positions, lead volumes, or revenue outcomes. Governed by the laws of the State of Florida. Contact: support@theordo.io`;

const PRIVACY = `PRIVACY POLICY — ORDO AI LLC — Last updated: February 2026

ORDO AI collects your business information to provide AI visibility scanning, GEO and SEO optimization, and outreach services. We do not sell your data to third parties. Contact: support@theordo.io`;

// ── Components ────────────────────────────────────────────────────────
const Logo = ({ onClick }) => (
  <a href="/" onClick={onClick} className="inline-flex items-baseline cursor-pointer hover:opacity-80 transition-opacity">
    <span className="text-xl font-black text-[#0F172A] font-['Outfit'] tracking-tight">ORDO AI</span>
  </a>
);

const PolicyModal = ({ title, onClose }) => {
  const content = title === "Terms of Service" ? TERMS : PRIVACY;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <h3 className="font-bold text-[#0F172A] font-['Outfit']">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-6 overflow-y-auto flex-1">
          <p className="text-xs text-slate-600 leading-relaxed">{content}</p>
        </div>
      </div>
    </div>
  );
};

// ── Scan Modal (3 steps) ──────────────────────────────────────────────
const ScanModal = ({ onClose, onComplete, initialUrl = "" }) => {
  const [step, setStep]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [otherText, setOtherText] = useState("");
  const [form, setForm] = useState({
    website_url: initialUrl,
    zip_code: "",
    business_category: "",
    business_subcategory: "",
    business_description: "",
  });

  const subcategories = form.business_category ? BUSINESS_CATEGORIES[form.business_category] || [] : [];
  const update = (f, v) => setForm((p) => {
    const u = { ...p, [f]: v };
    if (f === "business_category") u.business_subcategory = "";
    return u;
  });

  const next = () => {
    if (step === 1) {
      if (!form.website_url.trim()) { toast.error("Enter your website URL"); return; }
      setStep(2);
    } else if (step === 2) {
      if (!form.zip_code.trim())        { toast.error("Enter your ZIP code");          return; }
      if (!form.business_category)      { toast.error("Select a business category");   return; }
      if (!form.business_subcategory)   { toast.error("Select a business type");       return; }
      if (form.business_subcategory === "Other" && !otherText.trim()) {
        toast.error("Please specify your business type"); return;
      }
      setStep(3);
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const recaptcha_token = await getRecaptchaToken();
      const finalForm = {
        ...form,
        zip_code: form.zip_code || "00000",
        business_subcategory: form.business_subcategory === "Other" ? otherText : form.business_subcategory,
        recaptcha_token,
      };
      onComplete(finalForm);
    } catch {
      onComplete({ ...form, zip_code: form.zip_code || "00000", recaptcha_token: "error" });
    } finally {
      setLoading(false);
    }
  };

  const stepLabels = ["Your Website", "Business Info", "Brief Description"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-[#1E40AF] px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-white/60 text-xs font-medium mb-0.5">Step {step} of 3 — {stepLabels[step - 1]}</p>
            <h3 className="text-white font-bold font-['Outfit'] text-lg">Get Your Free ORDO AI Score</h3>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {/* Progress */}
        <div className="w-full bg-slate-100 h-1">
          <div className="bg-[#10B981] h-1 transition-all duration-500" style={{ width: `${(step / 3) * 100}%` }} />
        </div>
        <div className="p-6 space-y-4">

          {/* Step 1 — Website */}
          {step === 1 && (
            <>
              <p className="text-slate-600 text-sm">Enter your business website to scan your AI presence across 11 platforms</p>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Business Website URL</label>
                <input
                  value={form.website_url}
                  onChange={(e) => update("website_url", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && next()}
                  placeholder="https://yourbusiness.com"
                  className="w-full h-12 px-4 bg-slate-100 border border-slate-300 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none text-black placeholder-slate-400"
                  autoFocus
                />
              </div>
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-[#1E40AF] mb-2">Platforms we scan:</p>
                <div className="grid grid-cols-2 gap-1">
                  {AI_PLATFORMS.map((p) => (
                    <div key={p.name} className="flex items-center gap-1.5 text-xs text-slate-600">
                      <CheckCircle className="w-3 h-3 text-[#10B981]" /> {p.name}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Step 2 — Business Info */}
          {step === 2 && (
            <>
              <p className="text-slate-600 text-sm">Tell us about your business to get accurate scores</p>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">ZIP Code</label>
                <input
                  value={form.zip_code} onChange={(e) => update("zip_code", e.target.value)}
                  placeholder="e.g. 33101" maxLength={10}
                  className="w-full h-12 px-4 bg-slate-100 border border-slate-300 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none text-black placeholder-slate-400"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Business Category</label>
                <select
                  value={form.business_category} onChange={(e) => update("business_category", e.target.value)}
                  className="w-full h-12 px-4 bg-slate-100 border border-slate-300 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none text-black">
                  <option value="">Select category...</option>
                  {Object.keys(BUSINESS_CATEGORIES).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {form.business_category && (
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Business Type</label>
                  <select
                    value={form.business_subcategory} onChange={(e) => update("business_subcategory", e.target.value)}
                    className="w-full h-12 px-4 bg-slate-100 border border-slate-300 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none text-black">
                    <option value="">Select type...</option>
                    {subcategories.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
              {form.business_subcategory === "Other" && (
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Please specify</label>
                  <input
                    value={otherText} onChange={(e) => setOtherText(e.target.value)}
                    placeholder="Describe your business type..."
                    className="w-full h-12 px-4 bg-slate-100 border border-slate-300 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none text-black placeholder-slate-400"
                  />
                </div>
              )}
            </>
          )}

          {/* Step 3 — Description */}
          {step === 3 && (
            <>
              <p className="text-slate-600 text-sm">A brief description helps AI give you the most accurate results</p>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
                  Brief Business Description <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={form.business_description}
                  onChange={(e) => update("business_description", e.target.value)}
                  placeholder="E.g. We're a family-owned plumbing company in Miami serving residential clients since 2008..."
                  rows={4} maxLength={500}
                  className="w-full px-4 py-3 bg-slate-100 border border-slate-300 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none resize-none text-black placeholder-slate-400"
                  autoFocus
                />
                <p className="text-xs text-slate-400 text-right mt-1">{form.business_description.length}/500</p>
              </div>
            </>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)}
                className="px-5 py-3 border border-slate-200 rounded-xl text-sm text-slate-600 font-medium hover:bg-slate-50 transition-colors">
                Back
              </button>
            )}
            {step < 3 ? (
              <button onClick={next}
                className="flex-1 bg-[#10B981] hover:bg-[#059669] text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 transition-colors">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={loading}
                className="flex-1 bg-[#10B981] hover:bg-[#059669] text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                {loading
                  ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  : <><Bot className="w-4 h-4" /> Scan My Business</>
                }
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 text-center">3 days free trial · No setup fees</p>
        </div>
      </div>
    </div>
  );
};

// ── Main LandingPage ──────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScanModal, setShowScanModal]   = useState(false);
  const [scanModalUrl, setScanModalUrl]     = useState("");
  const [policyModal, setPolicyModal]       = useState(null);
  const [heroUrl, setHeroUrl]               = useState("");

  // Screens: landing | analyzing | results
  const [screen, setScreen]                       = useState("landing");
  const [analyzingPlatforms, setAnalyzingPlatforms] = useState([]);
  const [scanResults, setScanResults]             = useState(null);
  const [scanForm, setScanForm]                   = useState(null);

  // Plan pre-selected from Pricing page (passed via ?plan=basic/growth)
  const [preselectedPlan, setPreselectedPlan] = useState(null);

  // ── Detect ?scan=1 from Pricing page ─────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("scan") === "1") {
      const plan = params.get("plan");
      if (plan) setPreselectedPlan(plan);
      // Auto-open scan modal
      setShowScanModal(true);
      // Clean URL
      window.history.replaceState({}, "", "/");
    }
  }, []);

  // ── Scanning animation ────────────────────────────────────────────
  const runAnalyzing = () => {
    const p = AI_PLATFORMS.map((x) => ({ ...x, status: "pending" }));
    setAnalyzingPlatforms(p);
    AI_PLATFORMS.forEach((platform, i) => {
      setTimeout(() => {
        setAnalyzingPlatforms((prev) => prev.map((x, idx) => idx === i ? { ...x, status: "scanning" } : x));
        setTimeout(() => {
          setAnalyzingPlatforms((prev) => prev.map((x, idx) => idx === i ? { ...x, status: "done" } : x));
        }, platform.scanTime);
      }, platform.delay);
    });
  };

  // ── Hero URL submit ───────────────────────────────────────────────
  const handleHeroSubmit = () => {
    if (!heroUrl.trim()) { toast.error("Paste your website URL first"); return; }
    setScanModalUrl(heroUrl);
    setShowScanModal(true);
  };

  const openScan = (url = "") => {
    setScanModalUrl(url);
    setShowScanModal(true);
  };

  // ── After modal step 3 — run the actual scan ──────────────────────
  const handleScanComplete = async (form) => {
    setShowScanModal(false);
    setScanForm(form);
    setScreen("analyzing");
    runAnalyzing();

    try {
      const minDelay = new Promise((r) => setTimeout(r, TOTAL_SCAN_TIME));
      const res = await fetch(`${API}/scan/free`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          zip_code: form.zip_code || "00000",
        }),
      });
      await minDelay;
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem("pending_scan", JSON.stringify({ ...form, temp_id: data.temp_id }));
        setScanResults(data);
      } else {
        // Fallback score so user still sees results
        setScanResults({ master_score: Math.floor(Math.random() * 300) + 300 });
        localStorage.setItem("pending_scan", JSON.stringify(form));
      }
    } catch {
      setScanResults({ master_score: Math.floor(Math.random() * 300) + 300 });
      localStorage.setItem("pending_scan", JSON.stringify(form));
    }
    setScreen("results");
  };

  // ── Plan selection → signup ───────────────────────────────────────
  const choosePlan = (plan) => {
    localStorage.setItem("plan_intent", plan);
    navigate("/signup");
  };

  // ── Score helpers ─────────────────────────────────────────────────
  const scoreColor = (s) => {
    if (s >= 700) return { text: "text-[#10B981]", label: "Strong Visibility",   hex: "#10B981" };
    if (s >= 400) return { text: "text-[#F97316]", label: "Moderate Visibility", hex: "#F97316" };
    return           { text: "text-red-500",    label: "Low Visibility",       hex: "#EF4444" };
  };

  // ─────────────────────────────────────────────────────────────────
  // ANALYZING SCREEN
  // ─────────────────────────────────────────────────────────────────
  if (screen === "analyzing") return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-lg text-center">
        <Logo onClick={() => setScreen("landing")} />
        <div className="relative w-16 h-16 mx-auto mb-6 mt-8">
          <svg className="w-16 h-16 animate-spin" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="28" fill="none" stroke="#E2E8F0" strokeWidth="4" />
            <circle cx="32" cy="32" r="28" fill="none" stroke="#1E40AF" strokeWidth="4"
              strokeDasharray="176" strokeDashoffset="120" strokeLinecap="round" />
          </svg>
          <Bot className="w-7 h-7 text-[#1E40AF] absolute inset-0 m-auto" />
        </div>
        <h2 className="text-2xl font-bold text-[#0F172A] mb-2 font-['Outfit']">Scanning Your AI Presence</h2>
        <p className="text-slate-500 text-sm mb-8">{scanForm?.website_url}</p>
        <div className="grid grid-cols-2 gap-3">
          {analyzingPlatforms.filter(p => !p.isFinal).map((p, i) => (
            <div key={i}
              className="flex items-center gap-3 p-3 rounded-xl border transition-all duration-500"
              style={{
                borderColor: p.status === "done" ? "#10B981" : p.status === "scanning" ? "#1E40AF" : "#E2E8F0",
                background:  p.status === "done" ? "#f0fdf4" : p.status === "scanning" ? "#eff6ff" : "#fff",
              }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ background: p.bg, color: p.color, opacity: p.status === "pending" ? 0.4 : 1 }}>
                {p.name[0]}
              </div>
              <span className="text-sm text-slate-700 text-left">{p.name}</span>
              <span className="ml-auto flex-shrink-0">
                {p.status === "done"     ? <CheckCircle className="w-4 h-4 text-[#10B981]" /> :
                 p.status === "scanning" ? <div className="w-3 h-3 border-2 border-[#1E40AF] border-t-transparent rounded-full animate-spin" /> :
                 <div className="w-3 h-3 rounded-full bg-slate-200" />}
              </span>
            </div>
          ))}
        </div>
        {/* ORDO AI Score — final step, full width */}
        {analyzingPlatforms.filter(p => p.isFinal).map((p, i) => (
          <div key={i} className="mt-3 flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-500"
            style={{
              borderColor: p.status === "done" ? "#1E40AF" : p.status === "scanning" ? "#1E40AF" : "#E2E8F0",
              background:  p.status === "done" ? "#eff6ff" : p.status === "scanning" ? "#dbeafe" : "#f8fafc",
            }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0"
              style={{ background: p.status === "pending" ? "#E2E8F0" : "#1E40AF", color: "#fff", opacity: p.status === "pending" ? 0.5 : 1 }}>
              AI
            </div>
            <div className="flex-1">
              <span className="text-sm font-bold text-[#0F172A]">Computing ORDO AI Score</span>
              <p className="text-xs text-slate-500">Weighing all 11 platform scores…</p>
            </div>
            <span className="ml-auto flex-shrink-0">
              {p.status === "done"     ? <CheckCircle className="w-5 h-5 text-[#1E40AF]" /> :
               p.status === "scanning" ? <div className="w-4 h-4 border-2 border-[#1E40AF] border-t-transparent rounded-full animate-spin" /> :
               <div className="w-3 h-3 rounded-full bg-slate-200" />}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────
  // RESULTS SCREEN
  // ─────────────────────────────────────────────────────────────────
  if (screen === "results" && scanResults) {
    const sc  = scoreColor(scanResults.master_score);
    const pct = Math.min(scanResults.master_score / 1000, 1);
    const r   = 54;
    const circ = 2 * Math.PI * r;

    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <nav className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-6 sticky top-0 z-20">
          <Logo onClick={() => setScreen("landing")} />
          <button onClick={() => setScreen("landing")} className="text-sm text-[#1E40AF] hover:underline">
            ← Scan another business
          </button>
        </nav>

        <div className="max-w-2xl mx-auto px-6 py-10 space-y-5">

          {/* Score card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
            <p className="text-xs text-slate-500 font-semibold uppercase tracking-widest mb-4">Your ORDO AI Score</p>
            <p className="text-sm text-slate-500 mb-4 font-medium">{scanForm?.website_url}</p>
            <div className="relative w-36 h-36 mx-auto mb-4">
              <svg width="144" height="144" className="-rotate-90" viewBox="0 0 144 144">
                <circle cx="72" cy="72" r={r} fill="none" stroke="#E2E8F0" strokeWidth="10" />
                <circle cx="72" cy="72" r={r} fill="none" stroke={sc.hex} strokeWidth="10"
                  strokeDasharray={circ} strokeDashoffset={circ - pct * circ} strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 1.5s ease" }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-4xl font-bold font-['Outfit'] ${sc.text}`}>{scanResults.master_score}</span>
                <span className="text-xs text-slate-400">/1,000</span>
              </div>
            </div>
            <span className="text-sm font-bold px-4 py-1.5 rounded-full"
              style={{ color: sc.hex, background: sc.hex + "20" }}>
              {sc.label}
            </span>
            <p className="text-sm text-slate-500 mt-4 max-w-sm mx-auto">
              {scanResults.master_score < 400
                ? "Your business is nearly invisible to AI search engines. Most potential customers using ChatGPT, Gemini, or Perplexity won't find you."
                : scanResults.master_score < 700
                ? "You have some AI presence but significant gaps. Competitors with better optimization are capturing your potential customers."
                : "Strong AI presence! Let's maintain and grow it with automated optimization."}
            </p>
          </div>

          {/* Blurred platform breakdown */}
          <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm p-6 overflow-hidden">
            <p className="text-sm font-semibold text-[#0F172A] mb-4 font-['Outfit']">Platform Breakdown</p>
            <div className="grid grid-cols-2 gap-3 filter blur-sm select-none pointer-events-none">
              {AI_PLATFORMS.filter(p => !p.isFinal).slice(0, 6).map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold"
                    style={{ background: p.bg, color: p.color }}>
                    {p.name[0]}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-[#0F172A]">{Math.floor(Math.random() * 40) + 30}</div>
                    <div className="text-xs text-slate-500">{p.name}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-2xl">
              <p className="font-bold text-slate-700 text-sm">Subscribe to unlock full breakdown</p>
            </div>
          </div>

          {/* ── FULL PLAN COMPARISON (side by side) ── */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#1E40AF] px-6 py-5 text-center">
              <p className="text-white font-black font-['Outfit'] text-xl">Choose Your Plan</p>
              <p className="text-blue-200 text-xs mt-1">3 days free trial · No setup fees · Cancel anytime</p>
            </div>

            {/* Plan cards side by side */}
            <div className="grid grid-cols-2 gap-0 divide-x divide-slate-100">

              {/* BASIC */}
              <div className="p-5 flex flex-col">
                <div className="mb-4">
                  <div className="text-xs font-bold text-[#10B981] uppercase tracking-wider mb-1">Basic</div>
                  <div className="text-3xl font-black text-[#0F172A] font-['Outfit']">
                    $149<span className="text-sm font-normal text-slate-500">/mo</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">Scan, report & action plans. You execute</p>
                </div>
                <div className="bg-emerald-50 rounded-lg px-3 py-2 mb-4">
                  <p className="text-xs font-bold text-[#059669]">We automate $2,500 worth of biz dev</p>
                </div>
                <div className="space-y-2 flex-1">
                  {[
                    "Weekly AI score across 11 platforms",
                    "Full weekly score report",
                    "GEO and SEO visibility tracking",
                    "Action plan — you execute",
                    "Score history & leaderboard",
                    "Vendor & client discovery list",
                    "Outreach list with templates",
                    "PR pipeline action plan",
                    "Monthly GEO visibility report",
                  ].map((f, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-slate-600">{f}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => choosePlan("basic")}
                  className={`mt-5 w-full py-3 rounded-xl text-sm font-bold transition-all hover:scale-105 flex items-center justify-center gap-1 ${
                    preselectedPlan === "basic"
                      ? "bg-[#10B981] text-white shadow-md"
                      : "border-2 border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white"
                  }`}>
                  {preselectedPlan === "basic" && "★ "}Start Basic <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* GROWTH */}
              <div className="p-5 flex flex-col relative" style={{ background: "#f0f7ff" }}>
                <div className="absolute top-3 right-3">
                  <span className="text-xs font-bold bg-[#1E40AF] text-white px-2 py-0.5 rounded-full">Popular</span>
                </div>
                <div className="mb-4">
                  <div className="text-xs font-bold text-[#1E40AF] uppercase tracking-wider mb-1">Growth</div>
                  <div className="text-3xl font-black text-[#0F172A] font-['Outfit']">
                    $499<span className="text-sm font-normal text-slate-500">/mo</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">We run it all. You collect results</p>
                </div>
                <div className="bg-blue-100 rounded-lg px-3 py-2 mb-4">
                  <p className="text-xs font-bold text-[#1E40AF]">We automate $10,000 worth of biz dev</p>
                </div>
                <div className="space-y-2 flex-1">
                  <p className="text-xs font-bold text-[#1E40AF] mb-1">Everything in Basic, plus:</p>
                  {[
                    "Bot executes your action plan",
                    "GEO & SEO auto-optimized for you",
                    "We handle all outreach & citations",
                    "Vendor & client discovery — we send your contact",
                    "Lead gen fully automated",
                    "PR pipeline fully managed",
                    "Monthly done-for-you report",
                    "Live GEO Looker Studio dashboard",
                    "Priority support",
                  ].map((f, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5 text-[#1E40AF] flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-slate-700 font-medium">{f}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => choosePlan("growth")}
                  className={`mt-5 w-full py-3 rounded-xl text-sm font-bold transition-all hover:scale-105 flex items-center justify-center gap-1 ${
                    preselectedPlan === "growth"
                      ? "bg-[#1E40AF] text-white shadow-md"
                      : "bg-[#1E40AF] text-white hover:bg-[#1E3A8A]"
                  }`}>
                  {preselectedPlan === "growth" && "★ "}Start Growth <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Feature comparison table */}
            <div className="border-t border-slate-100 px-5 py-4">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Key Differences</p>
              <div className="space-y-2">
                {[
                  { feature: "Action plan",          basic: "You execute",       growth: "Bot executes" },
                  { feature: "Outreach & citations", basic: "You publish",       growth: "We handle it" },
                  { feature: "Lead generation",      basic: "List provided",     growth: "Fully automated" },
                  { feature: "GEO/SEO optimization", basic: "Report only",       growth: "Auto-optimized" },
                  { feature: "PR pipeline",          basic: "Plan only",         growth: "Fully managed" },
                ].map((row, i) => (
                  <div key={i} className="grid grid-cols-3 text-xs border-b border-slate-50 pb-2 last:border-0 last:pb-0">
                    <span className="text-slate-500">{row.feature}</span>
                    <span className="text-center text-slate-600">{row.basic}</span>
                    <span className="text-center font-semibold text-[#1E40AF]">{row.growth}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-5 pb-4 text-center">
              <p className="text-xs text-slate-400">
                Not sure? <a href="/pricing" className="text-[#1E40AF] hover:underline font-medium">See full plan details</a>
              </p>
            </div>
          </div>

          {/* Already have an account */}
          <div className="text-center">
            <p className="text-sm text-slate-500">
              Already have an account?{" "}
              <a href="/login" className="text-[#1E40AF] font-semibold hover:underline">Log in</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────
  // MAIN LANDING PAGE
  // ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-white font-['Outfit']">

      {/* NAV */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="hidden md:flex items-center gap-2">
            <a href="/pricing" className="text-sm font-medium text-slate-700 hover:text-[#1E40AF] px-3 py-2 transition-colors">Pricing</a>
            <a href="/login"   className="text-sm font-medium text-slate-700 hover:text-[#1E40AF] px-3 py-2 transition-colors">Log In</a>
            <button onClick={() => openScan()}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors">
              Free ORDO AI Score
            </button>
          </div>
          <div className="md:hidden flex items-center gap-2">
            <button onClick={() => openScan()}
              className="bg-[#F97316] text-white text-xs font-bold px-3 py-2 rounded-lg">
              Free Score
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-slate-600">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 px-4 py-3 space-y-2">
            <a href="/pricing" className="block text-sm font-medium text-slate-700 py-2">Pricing</a>
            <a href="/login"   className="block text-sm font-medium text-slate-700 py-2">Log In</a>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-[#1E40AF] text-xs font-bold px-4 py-2 rounded-full mb-8">
            <div className="w-1.5 h-1.5 rounded-full bg-[#1E40AF] animate-pulse" />
            Your Business AI Developer
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-[#0F172A] font-['Outfit'] leading-tight mb-6">
            Automate Your<br />
            <span className="text-[#1E40AF]">Business Growth with AI</span>
          </h1>
          <p className="text-lg text-slate-600 mb-10 max-w-xl mx-auto">
            You're not buying software. You're hiring an AI for your business development. Start your market domination today
          </p>

          {/* Hero URL input */}
          <div className="max-w-xl mx-auto mb-10">
            <div className="flex flex-col sm:flex-row gap-3 p-2 bg-slate-100 border border-slate-300 rounded-2xl">
              <input
                value={heroUrl}
                onChange={(e) => setHeroUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleHeroSubmit()}
                placeholder="Paste your website URL: https://..."
                className="w-full sm:flex-1 h-12 px-4 bg-white border border-slate-300 rounded-xl text-sm focus:border-[#10B981] focus:outline-none text-black placeholder-slate-400 min-w-0"
              />
              <button onClick={handleHeroSubmit}
                className="w-full sm:w-auto bg-[#10B981] hover:bg-[#059669] text-white font-bold px-5 py-3 rounded-xl text-sm whitespace-nowrap flex items-center justify-center gap-2 transition-colors">
                Get Free Score <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-slate-600 text-sm">
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4 text-[#10B981] flex-shrink-0" />
              <span>11 AI search platforms</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4 text-[#10B981] flex-shrink-0" />
              <span>1 in 5 consumers use AI to find local businesses</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4 text-[#10B981] flex-shrink-0" />
              <span>3 days free trial, no set up fee</span>
            </div>
          </div>
        </div>
      </section>

      {/* 3 PILLARS */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-bold text-[#F97316] uppercase tracking-widest mb-3">What ORDO AI Does</p>
            <h2 className="text-3xl md:text-4xl font-black text-[#0F172A] font-['Outfit']">
              Three AI Agents. One Platform. Running 24/7
            </h2>
            <p className="text-slate-600 mt-3 max-w-xl mx-auto text-sm">Deploy your agents once. Let them run. Watch your business grow</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden bg-white hover:shadow-lg transition-shadow">
                  <div className="h-1.5" style={{ background: p.color }} />
                  <div className="p-7">
                    <div className={`w-12 h-12 ${p.iconBg} rounded-xl flex items-center justify-center mb-5`}>
                      <Icon className="w-6 h-6" style={{ color: p.color }} />
                    </div>
                    <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: p.color }}>0{i + 1}</div>
                    <h3 className="text-lg font-bold text-[#0F172A] font-['Outfit'] mb-1">{p.title}</h3>
                    <p className="text-xs font-semibold text-slate-500 mb-3">{p.tagline}</p>
                    <p className="text-slate-600 text-sm leading-relaxed mb-6">{p.what}</p>
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold text-[#1E40AF] bg-blue-50 px-2 py-0.5 rounded-full">Basic</span>
                      </div>
                      <div className="space-y-1.5">
                        {p.basic.map((item, j) => (
                          <div key={j} className="flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-[#1E40AF] flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-slate-600">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="border-t border-slate-100 pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold text-[#F97316] bg-orange-50 px-2 py-0.5 rounded-full">⚡ Growth adds:</span>
                      </div>
                      <div className="space-y-1.5">
                        {p.growth.map((item, j) => (
                          <div key={j} className="flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-[#F97316] flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-slate-600">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* VALUE EQUATION */}
      <section className="py-20 bg-[#EFF6FF]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs font-bold text-[#1E40AF] uppercase tracking-widest mb-4">The Value Equation</p>
          <h2 className="text-4xl md:text-5xl font-black text-[#0F172A] font-['Outfit'] mb-4">
            We Automate <span className="text-[#EF4444]">$10,000</span> Worth of<br />
            Business Development<br />
            For Only <span className="text-[#10B981]">$499<span className="text-2xl font-bold">/mo</span></span>
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto text-sm leading-relaxed mt-4">
            Outreach. Citations. Lead generation. PR. AI search ranking. A full agency does this for $8,000–$15,000/month.
            ORDO AI deploys AI agents that do the same automatically, every single day
          </p>
          <div className="grid grid-cols-3 gap-4 mt-10 max-w-lg mx-auto">
            {[
              { label: "Traditional Agency", cost: "$8,000–$15,000/mo", color: "#EF4444" },
              { label: "Freelance Team",      cost: "$3,000–$6,000/mo",  color: "#F97316" },
              { label: "ORDO AI Growth",      cost: "$499/mo",           color: "#10B981" },
            ].map((item, i) => (
              <div key={i} className="rounded-xl border border-blue-100 p-4 bg-white shadow-sm">
                <div className="text-xs text-slate-500 mb-1">{item.label}</div>
                <div className="text-sm font-black font-['Outfit']" style={{ color: item.color }}>{item.cost}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPETITOR SECTION */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 text-red-500 text-xs font-bold px-4 py-2 rounded-full mb-6 border border-red-200 bg-red-50">
            <TrendingUp className="w-3.5 h-3.5" /> Market Reality
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-[#0F172A] font-['Outfit'] mb-4">
            Your Competitors are Already Adapting AI Automation.<br />
            <span className="text-red-500">Are you?</span>
          </h2>
          <p className="text-slate-600 text-lg mb-10 max-w-2xl mx-auto">
            Modern businesses run on automation. AI search, voice assistants, directories, and digital PR platforms constantly
            recommend certain companies. Deploy ORDO AI in minutes and put your growth on autopilot
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            {[
              { stat: "11 platforms", label: "AI engines recommending local businesses right now" },
              { stat: "1 in 5",       label: "consumers use AI or voice assistant to find a local business" },
              { stat: "3x faster",    label: "growth for businesses with strong AI visibility" },
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                <div className="text-2xl font-black text-[#1E40AF] font-['Outfit'] mb-1">{item.stat}</div>
                <div className="text-xs text-slate-600">{item.label}</div>
              </div>
            ))}
          </div>
          <button onClick={() => openScan()}
            className="bg-[#10B981] hover:bg-[#059669] text-white font-bold px-8 py-4 rounded-xl text-base inline-flex items-center gap-2 transition-colors">
            See Where You Stand — Free <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-20 bg-[#EFF6FF] border-t border-blue-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-black text-[#0F172A] font-['Outfit'] mb-4">
            Start Your Market Domination Today
          </h2>
          <p className="text-slate-600 text-lg mb-10">Scan free. Choose your plan. Deploy your AI agents. Watch your business grow</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => openScan()}
              className="bg-[#10B981] hover:bg-[#059669] text-white font-bold px-10 py-4 rounded-xl text-base inline-flex items-center justify-center gap-2 transition-colors shadow-lg shadow-green-100">
              Get My Free Score <ArrowRight className="w-5 h-5" />
            </button>
            <a href="/pricing"
              className="border border-slate-300 hover:border-slate-400 bg-white text-[#0F172A] font-semibold px-10 py-4 rounded-xl text-base text-center transition-colors">
              View Plans
            </a>
          </div>
          <p className="text-slate-500 text-sm mt-6">3 days free trial · No setup fee · Cancel anytime</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white border-t border-slate-200 py-6">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo />
            <span className="text-slate-500 text-xs">Your Business AI Developer</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-slate-500">
            <button onClick={() => setPolicyModal("Terms of Service")} className="hover:text-[#1E40AF] transition-colors">Terms of Service</button>
            <button onClick={() => setPolicyModal("Privacy Policy")}   className="hover:text-[#1E40AF] transition-colors">Privacy Policy</button>
            <a href="mailto:support@theordo.io" className="hover:text-[#1E40AF] transition-colors">support@theordo.io</a>
          </div>
          <p className="text-xs text-slate-500">© 2026 ORDO AI LLC</p>
        </div>
      </footer>

      {showScanModal && (
        <ScanModal
          onClose={() => setShowScanModal(false)}
          onComplete={handleScanComplete}
          initialUrl={scanModalUrl}
        />
      )}
      {policyModal && <PolicyModal title={policyModal} onClose={() => setPolicyModal(null)} />}
    </div>
  );
};

export default LandingPage;
